import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, IconButton, Box, Container,
  Drawer, List, ListItem, ListItemButton, ListItemText, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import NightlightRoundRoundedIcon from '@mui/icons-material/NightlightRoundRounded';
import { Link } from 'react-scroll';
import { motion, useScroll, useSpring } from 'motion/react';
import { content } from '../../data/content';

interface HeaderProps {
  toggleColorMode: () => void;
  mode: 'light' | 'dark';
}

const MotionToolbar = motion.create(Toolbar);
const MotionBox    = motion.create(Box);

const Header: React.FC<HeaderProps> = ({ toggleColorMode, mode }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);
  const theme = useTheme();
  const { navItems, logo, cta } = content.header;

  // Scroll progress bar
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 200, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinkStyle: React.CSSProperties = {
    cursor:         'pointer',
    fontFamily:     '"Space Grotesk", "Inter", sans-serif',
    fontWeight:     500,
    fontSize:       '14px',
    textDecoration: 'none',
    color:          theme.palette.text.secondary,
    transition:     'color 200ms ease',
    letterSpacing:  '0.01em',
  };

  const drawer = (
    <Box sx={{ width: 270, height: '100%', bgcolor: 'background.paper', py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, mb: 2 }}>
        <Box component="span" sx={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontWeight: 700,
          fontSize:   '1.25rem',
          color:      'text.primary',
          letterSpacing: '-0.02em',
        }}>
          <Box component="span" sx={{ color: 'primary.main' }}>{logo.initial}</Box>
          {logo.rest}
        </Box>
        <IconButton onClick={() => setMobileOpen(false)} size="small" sx={{ color: 'text.secondary', cursor: 'pointer' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <List disablePadding>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <Link to={item.to} spy smooth offset={-70} duration={500} onClick={() => setMobileOpen(false)} style={{ width: '100%' }}>
              <ListItemButton sx={{
                px: 3, py: 1.5,
                color: 'text.secondary',
                cursor: 'pointer',
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
              }}>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontWeight: 500,
                    fontSize:   '0.9375rem',
                  }}
                />
              </ListItemButton>
            </Link>
          </ListItem>
        ))}
      </List>
      <Box sx={{ px: 3, mt: 3 }}>
        <Link to="contact" spy smooth offset={-70} duration={500} onClick={() => setMobileOpen(false)}>
          <Button variant="contained" fullWidth size="medium" sx={{ cursor: 'pointer' }}>{cta}</Button>
        </Link>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Scroll progress bar */}
      <MotionBox
        style={{ scaleX }}
        sx={{
          position:        'fixed',
          top:             0,
          left:            0,
          right:           0,
          height:          '2px',
          background:      `linear-gradient(90deg, ${theme.palette.primary.main}, #a78bfa, #34d399)`,
          transformOrigin: 'left',
          zIndex:          (theme) => theme.zIndex.appBar + 1,
          pointerEvents:   'none',
        }}
      />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          borderBottom:  scrolled ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
          transition:    'border-color 300ms ease, background-color 300ms ease',
        }}
      >
        <Container maxWidth="lg">
          <MotionToolbar
            disableGutters
            sx={{ minHeight: { xs: 60, md: 68 } }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Logo */}
            <Box sx={{
              fontFamily:    '"Space Grotesk", sans-serif',
              fontWeight:    700,
              fontSize:      '1.4rem',
              color:         'text.primary',
              letterSpacing: '-0.03em',
              userSelect:    'none',
              mr:            4,
              cursor:        'default',
            }}>
              <Box component="span" sx={{
                color:      'primary.main',
                textShadow: `0 0 24px ${theme.palette.primary.main}80`,
              }}>
                {logo.initial}
              </Box>
              {logo.rest}
            </Box>

            {/* Desktop nav */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: 3.5 }}>
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  spy smooth offset={-80} duration={500}
                  style={navLinkStyle}
                  activeStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                >
                  {item.label}
                </Link>
              ))}
            </Box>

            {/* Right actions */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5 }}>
              <IconButton
                onClick={toggleColorMode}
                size="small"
                aria-label="Toggle color mode"
                sx={{
                  color:      'text.secondary',
                  cursor:     'pointer',
                  '&:hover':  { color: 'primary.main', bgcolor: `${theme.palette.primary.main}14` },
                }}
              >
                {mode === 'dark'
                  ? <WbSunnyRoundedIcon fontSize="small" />
                  : <NightlightRoundRoundedIcon fontSize="small" />}
              </IconButton>
              <Link to="contact" spy smooth offset={-80} duration={500}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ px: 2.5, fontSize: '0.875rem', cursor: 'pointer' }}
                >
                  {cta}
                </Button>
              </Link>
            </Box>

            {/* Mobile actions */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 'auto', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                onClick={toggleColorMode}
                size="small"
                aria-label="Toggle color mode"
                sx={{ color: 'text.secondary', cursor: 'pointer' }}
              >
                {mode === 'dark'
                  ? <WbSunnyRoundedIcon fontSize="small" />
                  : <NightlightRoundRoundedIcon fontSize="small" />}
              </IconButton>
              <IconButton
                aria-label="Open navigation menu"
                onClick={() => setMobileOpen(true)}
                sx={{ color: 'text.secondary', cursor: 'pointer' }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </MotionToolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
